import { Link } from '@tanstack/react-router'
import { Badge } from '@/shared/components/ui/Badge'
import { buttonClass } from '@/shared/components/ui/Button'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { SurvivorGameStatus } from '@/features/sobrevivente/types'
import type { SurvivorGameListItem } from '@/features/sobrevivente/types'
import { formatFirstMatchIn, pickTone } from '../lib/survivorHome'
import type { PickTone } from '../lib/survivorHome'
import styles from './SurvivorRoundCard.module.css'

type SurvivorRoundCardProps = {
  game: SurvivorGameListItem
}

const PICK_TONE_KEY = {
  won:     'survivor.pick.won',
  lost:    'survivor.pick.lost',
  live:    'survivor.pick.live',
  pending: 'survivor.pick.pending',
} as const satisfies Record<PickTone, string>

const PICK_TONE_BADGE = {
  won:     'alive',
  lost:    'out',
  live:    'live',
  pending: 'neutral',
} as const satisfies Record<PickTone, 'alive' | 'out' | 'live' | 'neutral'>

/**
 * One survivor game on the home screen: who you are up against, which round is
 * in play, and what you picked. No card fill — a rule separates it from the
 * next game, the way a fixture list separates matches.
 */
export function SurvivorRoundCard({ game }: SurvivorRoundCardProps) {
  const { t } = useTranslation()

  return (
    <article className={styles.row}>
      <div className={styles.head}>
        <Link
          to="/sobrevivente/$gameId"
          params={{ gameId: game.id }}
          search={{ gw: undefined }}
          className={styles.gameName}
        >
          {game.name}
        </Link>
        <span className={styles.aliveCount}>
          {game.aliveCount}
          <span className={styles.aliveOf}>/{game.participantCount}</span>
        </span>
      </div>

      <div className={styles.subhead}>
        <span className={styles.competitionName}>{game.competitionName}</span>
        <MyStatusBadge game={game} />
      </div>

      <RoundLine game={game} t={t} />
    </article>
  )
}

function MyStatusBadge({ game }: { game: SurvivorGameListItem }) {
  const { t } = useTranslation()

  if (game.isChampion) {
    return <Badge tone="money" variant="soft">{t('survivor.status.champion')}</Badge>
  }
  if (!game.isAlive) {
    return <Badge tone="out" variant="soft">{t('survivor.status.eliminated')}</Badge>
  }
  return <Badge tone="alive" variant="outline" hasDot>{t('survivor.status.alive')}</Badge>
}

function RoundLine({
  game,
  t,
}: {
  game: SurvivorGameListItem
  t: ReturnType<typeof useTranslation>['t']
}) {
  const round = game.currentRound

  if (!round) {
    return <p className={styles.roundEmpty}>{t('survivor.round.noCalendar')}</p>
  }

  const hasPick    = round.myPickTeamName !== undefined
  const tone       = pickTone(round.myPickResult, round.myPickLiveResult)
  const firstIn    = formatFirstMatchIn(round.firstMatchAt)
  const isFinished = game.status === SurvivorGameStatus.Finished

  return (
    <div className={styles.round}>
      <div className={styles.roundHead}>
        <span className={styles.roundLabel}>
          {t('survivor.round.short', { number: round.gameweekNumber })}
        </span>
        {!isFinished && round.isPickLocked && (
          <span className={styles.roundMeta}>{t('survivor.round.locked')}</span>
        )}
        {!isFinished && firstIn && !round.isPickLocked && (
          <span className={styles.roundDeadline}>{firstIn}</span>
        )}
      </div>

      {hasPick ? (
        <div className={styles.pickRow}>
          <span className={styles.pickTeam}>{round.myPickTeamName}</span>
          <Badge tone={PICK_TONE_BADGE[tone]} variant="soft">
            {t(PICK_TONE_KEY[tone])}
          </Badge>
        </div>
      ) : game.isAlive && !isFinished ? (
        <div className={styles.pickRow}>
          <span className={styles.pickMissing}>
            {round.isPickLocked ? t('survivor.pick.missed') : t('survivor.pick.pendingCta')}
          </span>
          {!round.isPickLocked && (
            <Link
              to="/sobrevivente/$gameId"
              params={{ gameId: game.id }}
              search={{ gw: undefined }}
              className={buttonClass({ variant: 'secondary', size: 'sm' })}
            >
              {t('survivor.pick.choose')}
            </Link>
          )}
        </div>
      ) : null}
    </div>
  )
}
